<template>
	<div class="items_card">
		<div class="card_header">
			<span class="card_title">{{ categoryPlural }}</span>
			<span class="card_count">{{ items.length }}</span>
		</div>

		<!-- Encabezado de columnas: con dos porcentajes por fila (participación
			en la venta y margen) hace falta decir cuál es cuál. -->
		<div class="column_head">
			<span>Venta</span>
			<span>Part.</span>
			<span>Margen</span>
		</div>

		<!-- Fila "todas": vuelve al mes completo de la categoría, igual que el
			título de la cascada de cuentas vuelve a todas las contrapartes. -->
		<button type="button" class="item_row item_row--all"
			:class="{ 'is-selected': !selectedItem }"
			@click="$emit('select-item', null)">
			<span class="item_label">{{ allLabel }}</span>
			<span class="item_amount">{{ formatMoney(total) }}</span>
			<span class="item_share"></span>
			<span class="item_margin"></span>
		</button>

		<VirtualList v-if="items.length > 0"
			class="items_list"
			tag="ul"
			row-tag="li"
			:items="items"
			:row-height="ROW_HEIGHT"
			:row-key="(item) => item.name">
			<template #row="{ item }">
				<button type="button" class="item_row"
					:class="{ 'is-selected': item.name === selectedItem }"
					@click="$emit('select-item', item.name)">
					<span class="item_dot" :style="{ background: item.color }"></span>
					<span class="item_label"
						v-tooltip.top="item.name.length > 18 ? item.name : ''">
						{{ item.name }}
					</span>
					<span class="item_track">
						<span class="item_fill"
							:class="{ 'item_fill--negative': item.share < 0 }"
							:style="{ width: barWidth(item.share), backgroundColor: item.color }"></span>
					</span>
					<span class="item_amount">{{ formatMoney(item.sales) }}</span>
					<span class="item_share">{{ formatShare(item.share) }}</span>
					<span class="item_margin"
						:class="{ 'is_negative': item.margin_pct < 0 }">
						{{ item.margin_pct }}%
					</span>
				</button>
			</template>
		</VirtualList>

		<p v-else class="empty_state">Sin {{ categoryPlural }} con movimientos en este mes.</p>

	</div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import { formatShare, shareBarWidth } from "@/utils/percentage";
import VirtualList from "@/components/generics/VirtualList.vue";

// Card izquierda de la sección "Costos y contrapartes" en su modo por
// categoría: los ÍTEMS de la categoría con movimiento en el mes (tiendas,
// marcas, centros de costo), cada uno con su venta, su participación y su
// margen. Elegir una fila acota el detalle —las contrapartes de al lado y,
// al cambiar de modo, la cascada de cuentas— a ese ítem; "todas" vuelve al
// mes completo.
//
// El orden y el color son los mismos que en las cards y en la evolución, así
// una tienda se reconoce igual en todo el drawer. Virtualizada: una categoría
// puede traer decenas de ítems.
const ROW_HEIGHT = 36;

export default {
	name: "CategoryItemsCard",

	components: { VirtualList },

	props: {
		// Filas del backend (`items_breakdown`) con su color ya resuelto:
		// [{ name, sales, share, margin_pct, color }].
		items: { type: Array, default: () => [] },
		// Nombre de la categoría en plural, como lo escribe el analista.
		categoryPlural: { type: String, default: "ítems" },
		// Ítem elegido. Null = todas.
		selectedItem: { type: String, default: null },
		// Venta del mes de la categoría completa (la fila "todas").
		total: { type: Number, default: 0 },
	},

	data() {
		return { ROW_HEIGHT };
	},

	computed: {
		allLabel() {
			return `Todas las ${this.categoryPlural}`;
		},
	},

	methods: {
		formatMoney(value) {
			return parseToMoney(value);
		},
		formatShare(share) {
			return formatShare(share);
		},
		// Share signado: la barra pinta la magnitud y el número, el signo.
		barWidth(share) {
			return shareBarWidth(share);
		},
	},
};
</script>

<style scoped lang="scss">
// punto · nombre · barra · venta · participación · margen. Cada número tiene
// su columna de ancho fijo: en una sola celda quedaban escalonados detrás de
// montos de distinto largo. La comparte el encabezado de columnas.
$item-grid: 8px minmax(0, 132px) 1fr 104px 42px 52px;

.items_card {
	background: var(--report-bg);
	border-radius: 16px;
	border: 1px solid var(--report-border);
	box-shadow: var(--report-shadow-card);
	padding: 20px;
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.card_header {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 12px;
	flex-shrink: 0;
}

.card_title {
	font-size: 14px;
	font-weight: 600;
	color: var(--report-text);
	text-transform: capitalize;
}

// Cuántos ítems tuvieron movimiento en el mes.
.card_count {
	font-size: 13px;
	font-weight: 500;
	color: var(--report-text-muted);
	font-variant-numeric: tabular-nums;
}

.column_head {
	display: grid;
	grid-template-columns: $item-grid;
	gap: 10px;
	padding: 0 8px 6px;
	border-bottom: 1px solid var(--report-divider-soft);
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.02em;
	color: var(--report-text-muted);
	text-align: right;

	// Las tres etiquetas viven en las últimas columnas; las tres primeras
	// (punto, nombre, barra) quedan vacías.
	span:first-child {
		grid-column: 4;
	}
}

.items_list {
	flex: 1;
	min-height: 0;
}

.item_row {
	display: grid;
	grid-template-columns: $item-grid;
	align-items: center;
	gap: 10px;
	width: 100%;
	height: 100%;
	box-sizing: border-box;
	padding: 6px 8px;
	border: 0;
	border-radius: 8px;
	background: none;
	font-family: inherit;
	font-size: 13px;
	text-align: left;
	cursor: pointer;
	transition: background-color 120ms ease;

	&:hover {
		background: var(--report-surface);
	}

	&:focus-visible {
		outline: 2px solid var(--report-accent-border);
		outline-offset: 2px;
	}

	&.is-selected {
		background: var(--report-accent-bg);
	}
}

// La fila "todas" no tiene barra ni punto: es el encabezado del universo. Su
// monto cae en la misma columna que el de los ítems.
.item_row--all {
	grid-template-columns: 1fr 104px 42px 52px;
	height: 36px;
	margin-bottom: 4px;
	flex-shrink: 0;
	font-weight: 600;
}

.item_dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
}

.item_label {
	color: var(--report-text);
	font-weight: 500;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.item_track {
	height: 8px;
	border-radius: 999px;
	background: var(--report-progress-bg);
	overflow: hidden;
}

.item_fill {
	display: block;
	height: 100%;
	border-radius: 999px;
	transition: width 200ms ease-out;
}

// Un ítem con costo negativo (una NC del mes) conserva su color, rayado.
.item_fill--negative {
	background-image: repeating-linear-gradient(
		-45deg,
		rgba(255, 255, 255, 0.65) 0 2px,
		transparent 2px 5px
	);
}

.item_amount,
.item_share,
.item_margin {
	text-align: right;
	white-space: nowrap;
	font-variant-numeric: tabular-nums;
}

.item_amount {
	color: var(--report-text-muted-strong);
	font-weight: 500;
}

.item_share {
	color: var(--report-text-muted);
}

// El margen del ítem: en rojo cuando la tienda pierde plata.
.item_margin {
	color: var(--report-text-strong-2);
	font-weight: 500;

	&.is_negative {
		color: var(--report-negative);
	}
}

.empty_state {
	color: var(--report-text-muted);
	font-size: 13px;
	text-align: center;
	padding: 24px 8px;
}
</style>
